import React from 'react';
import { Link } from 'react-router-dom';
import './AboutUs.css';
import { FaBullseye, FaClock, FaGithub, FaLinkedinIn, FaSearch } from 'react-icons/fa';

const AboutUs = () => {
  return (
    <div className="about-page">
      {/* SVG Definitions for Patterns */}
      <svg 
        width="0" 
        height="0" 
        style={{ position: 'absolute' }}
      >
        <defs>
          <pattern 
            id="gridPattern" 
            width="20" 
            height="20" 
            patternUnits="userSpaceOnUse"
          >
            <path 
              d="M 20 0 L 0 0 0 20" 
              fill="none" 
              stroke="white" 
              strokeWidth="0.5" 
              opacity="0.3"
            />
          </pattern>
        </defs>
      </svg>

      <div className="about-hero">
        <div className="about-hero__overlay">
          <svg 
            width="100%" 
            height="100%" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect 
              width="100%" 
              height="100%" 
              fill="url(#gridPattern)" 
            />
          </svg>
        </div>
        <div className="about-container">
          <h1 className="about-hero__title">About OppLiy & CVance</h1>
          <p className="about-hero__subtitle">Simplifying job searches and optimizing applications for today's job seekers</p>
        </div>
      </div>

      <div className="about-container">
        <section className="about-mission">
          <div className="about-mission__content">
            <h2 className="about-section__title">Our Vision</h2>
            <p className="about-mission__text">
              At CVance and OppLiy, we envision a world where job seekers can focus more on their 
              careers and less on the tedious job hunting process. We believe that finding and 
              applying to jobs shouldn't be a full-time job in itself. Our mission is to help 
              professionals spend less time searching and preparing job applications, and more 
              time excelling in their actual work.
            </p>
            <div className="about-mission__description">
              <p>
                <strong>CVance</strong> is a resume tailoring platform that optimizes your existing resume 
                for specific job descriptions without altering your core information. We create 
                ATS-friendly versions that highlight relevant skills and experiences, improving 
                your chances of getting noticed by hiring managers.
              </p>
              <p>
                <strong>OppLiy</strong> is our job listing platform that aggregates job postings from across 
                the United States, providing a centralized resource for job seekers to discover 
                opportunities that match their qualifications and aspirations.
              </p>
            </div>
            <div className="about-mission__stats">
              <div className="stat-box">
                <span className="stat-number">100%</span>
                <span className="stat-label">ATS-Friendly</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">50%</span>
                <span className="stat-label">Less time spent job hunting</span>
              </div>
              <div className="stat-box">
                <span className="stat-number">2in1</span>
                <span className="stat-label">Platform solution</span>
              </div>
            </div>
          </div>
          <div className="about-mission__graphic">
            <div className="about-shape-1"></div>
            <div className="about-shape-2"></div>
          </div>
        </section>

        <section className="about-story">
          <h2 className="about-section__title">Our Story</h2>
          <p className="about-story__intro">
            We're just at the beginning of our journey. In 2025, we identified a critical 
            gap in the job market: job seekers were spending countless hours searching for 
            positions and then even more time customizing their applications for each opportunity. 
            This inefficiency inspired us to create a dual solution that addresses both problems.
          </p>
          <div className="about-story__vision">
            <div className="vision-item">
              <div className="vision-icon">
                <FaClock />
              </div>
              <div className="vision-content">
                <h3 className="vision-title">Time Efficiency</h3>
                <p className="vision-text">
                  We're committed to reducing the hours job seekers spend hunting for 
                  opportunities and preparing application materials. Our platforms automate 
                  and streamline these processes so you can focus on what matters.
                </p>
              </div>
            </div>
            
            <div className="vision-item">
              <div className="vision-icon">
                <FaBullseye />
              </div>
              <div className="vision-content">
                <h3 className="vision-title">Precision Matching</h3>
                <p className="vision-text">
                  Our algorithms ensure that your resume emphasizes the most relevant skills 
                  and experiences for each job, increasing your chances of making it through 
                  automated screening systems and catching the hiring manager's attention.
                </p>
              </div>
            </div>
            
            <div className="vision-item">
              <div className="vision-icon">
                <FaSearch />
              </div>
              <div className="vision-content">
                <h3 className="vision-title">Comprehensive Job Discovery</h3>
                <p className="vision-text">
                  Through OppLiy, we aim to provide the most comprehensive job listing platform, 
                  bringing opportunities from across the country into one accessible location to 
                  save you from having to visit multiple job boards.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="about-founders">
          <h2 className="about-section__title about-section__title--centered">The Founders</h2>
          <div className="founders-container">
            <div className="founder-card">
              <div className="founder-card__image-container">
                <div className="founder-card__image founder-card__image--venkata"></div>
                <div className="founder-card__decoration founder-card__decoration--top-right"></div>
                <div className="founder-card__decoration founder-card__decoration--bottom-left"></div>
              </div>
              <div className="founder-card__content">
                <h3 className="founder-card__name">Venkata Sai Reddy Punnam</h3>
                <p className="founder-card__title">Founder & CEO</p>
                <p className="founder-card__specialties">Full Stack Developer, AI Engineer, ML Engineer</p>
                <p className="founder-card__bio">
                  With extensive expertise in artificial intelligence and full-stack development, 
                  Venkata leads the technical vision for CVance and OppLiy. His passion for creating 
                  efficient, user-friendly solutions drives our platform development, focusing on 
                  resume optimization algorithms and job matching systems.
                </p>
                <div className="founder-card__social">
                  <a href="https://linkedin.com/in/venkatasaipunnam" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaLinkedinIn />
                  </a>
                  <a href="https://github.com/venkatasaipunnam" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaGithub />
                  </a>
                </div>
              </div>
            </div>

            <div className="founder-card">
              <div className="founder-card__image-container">
                <div className="founder-card__image founder-card__image--santhi"></div>
                <div className="founder-card__decoration founder-card__decoration--top-right"></div>
                <div className="founder-card__decoration founder-card__decoration--bottom-left"></div>
              </div>
              <div className="founder-card__content">
                <h3 className="founder-card__name">Santhi Prasanna Y</h3>
                <p className="founder-card__title">Co-Founder & Business Analyst</p>
                <p className="founder-card__specialties">Strategic Planning, Market Analysis, Product Management</p>
                <p className="founder-card__bio">
                  Santhi brings strong analytical expertise to CVance and OppLiy as our Business Analyst. 
                  Her strategic vision and deep understanding of job market landscapes guide our 
                  product development, ensuring that our platforms address real user needs and 
                  deliver tangible benefits to job seekers.
                </p>
                <div className="founder-card__social">
                  <a href="https://linkedin.com/in/santhiprasanna" target="_blank" rel="noopener noreferrer" className="social-icon">
                    <FaLinkedinIn />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="about-values">
          <h2 className="about-section__title about-section__title--light">Our Core Values</h2>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-card__icon">
                <i className="fas fa-clock-rotate-left"></i>
              </div>
              <h3 className="value-card__title">Time Efficiency</h3>
              <p className="value-card__description">
                We believe your time is valuable. Our platforms are designed to minimize the 
                hours spent on job searching and application preparation.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-card__icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 className="value-card__title">Data Integrity</h3>
              <p className="value-card__description">
                We respect your information and never alter or modify your resume content—we 
                simply optimize its presentation for specific job opportunities.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-card__icon">
                <i className="fas fa-users"></i>
              </div>
              <h3 className="value-card__title">Accessibility</h3>
              <p className="value-card__description">
                We believe quality career tools should be accessible to everyone, regardless of 
                background or experience level.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-card__icon">
                <i className="fas fa-rocket"></i>
              </div>
              <h3 className="value-card__title">Innovation</h3>
              <p className="value-card__description">
                We continuously improve our platforms with cutting-edge technology to provide the 
                most effective job search and resume tailoring experience.
              </p>
            </div>
          </div>
        </section>

        <section className="about-join">
          <div className="about-join__content">
            <h2 className="about-join__title">Ready to Streamline Your Job Search?</h2>
            <p className="about-join__text">
              Join CVance and OppLiy to spend less time on job hunting and application 
              preparation, and more time focusing on what you do best.
            </p>
            <div className="about-join__buttons">
              <Link to="/register" className="about-button about-button--primary">Get Started</Link>
              <Link to="/contact" className="about-button about-button--secondary">Contact Us</Link>
            </div>
          </div>
        </section>

        <section className="about-disclaimer">
          <div className="disclaimer-content">
            <h3 className="disclaimer-title">Project Status Disclaimer</h3>
            <p className="disclaimer-text">
              CVance and OppLiy are currently in the project development phase. We are not a registered 
              company or LLC at this time. This is a non-profit project aimed at addressing challenges 
              in the job search process. The platforms and services described are under development and 
              subject to change. Any information provided is for informational purposes only and does not 
              constitute a commercial offering or binding agreement.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;